import m from 'mithril';
import {
    asCurrency,
    asPercent,
    yearsMonths,
    yearsMonthsLong,
} from './format.ts';
import { heading } from './hint.ts';
import { NumberInput } from './input.ts';
import { Page } from './layout.ts';
import { ScrollHint } from './scroll_hint.ts';
import { Schedule } from './schedule.ts';
import { appreciator } from './lib/appreciation.ts';
import { forMonthlyCost } from './lib/mortgage.ts';
import { getBestMortgageDetail } from './lib/mortgage_detail.ts';
import { RATES_AS_AT } from './lib/rates.ts';
import type {
    AnyMortgageLifetime,
    EconomyContext,
    Scenario as ScenarioData,
    ScenarioContext,
} from './lib/scenario.ts';
import {
    compareScenarios,
    halfStepPurchase,
    isErrored,
    netWorthAtHorizon,
    simplePurchase,
    totalLoanCost,
} from './lib/scenario.ts';

interface Option {
    title: string;
    subtitle: string;
    wide: boolean;
    scenario: ScenarioData;
}

let scenarios: Option[] = [];

/** House price growth the old backend hardcoded when wiring EconomyContext. */
const APPRECIATION_RATE = 0.04;
const APPRECIATOR = appreciator(APPRECIATION_RATE);

/** How far each "what if you changed one number" option moves that number. */
const CHEAPER_HOUSE = 0.05;
const BIGGER_PAYMENT = 0.1;
const BIGGER_DEPOSIT = 0.05;

interface Strategy {
    title: string;
    /** The line under the title, from the numbers actually in the form. */
    subtitle: (scenario_context: ScenarioContext) => string;
    /** Set by the one case that runs two mortgages and so needs two cards. */
    wide?: boolean;
    build: (
        economy_context: EconomyContext,
        scenario_context: ScenarioContext,
    ) => ScenarioData;
}

/**
 * One of the "what if you changed a number" options: the same purchase as the
 * base case with a single input nudged.
 *
 * `tweak` is written once and drives both the sum and the subtitle, so the
 * "£237,500 instead of £250,000" under the heading can never describe a
 * different change from the one the card was costed with.
 */
function alternative(
    title: string,
    tweak: (context: ScenarioContext) => ScenarioContext,
    describe: (nudged: ScenarioContext, context: ScenarioContext) => string,
): Strategy {
    return {
        title,
        subtitle: (context) => describe(tweak(context), context),
        build: (economy_context, context) =>
            simplePurchase(economy_context, tweak(context)),
    };
}

/**
 * The base case first, then the alternatives, in display order. Titles live
 * here rather than in a second array beside them, so an option cannot end up
 * labelled as the one next to it. Percentages come from the constants above so
 * a heading and the sum it describes cannot drift apart.
 *
 * Everything below the base case is measured against it: the question each one
 * answers is "what would that change buy me", not "which of these five wins".
 */
const STRATEGIES: Strategy[] = [
    {
        title: 'Buy the house you want, now',
        subtitle: () => 'The case the others are measured against.',
        build: simplePurchase,
    },
    alternative(
        `Buy a house ${asPercent(CHEAPER_HOUSE)} cheaper`,
        (context) => ({
            ...context,
            house_price: Math.round(context.house_price * (1 - CHEAPER_HOUSE)),
        }),
        (nudged, context) =>
            `${asCurrency(nudged.house_price)} instead of ${asCurrency(
                context.house_price,
            )}`,
    ),
    alternative(
        `Pay ${asPercent(BIGGER_PAYMENT)} more each month`,
        (context) => ({
            ...context,
            monthly_payment: Math.round(
                context.monthly_payment * (1 + BIGGER_PAYMENT),
            ),
        }),
        (nudged, context) =>
            `${asCurrency(nudged.monthly_payment)} a month instead of ${asCurrency(
                context.monthly_payment,
            )}`,
    ),
    alternative(
        `Find ${asPercent(BIGGER_DEPOSIT)} more deposit`,
        (context) => ({
            ...context,
            deposit: Math.round(context.deposit * (1 + BIGGER_DEPOSIT)),
        }),
        (nudged, context) =>
            `${asCurrency(nudged.deposit)} down instead of ${asCurrency(
                context.deposit,
            )}`,
    ),
    {
        // Last because it is the most involved of them: two houses, two
        // mortgages, and a sale in the middle.
        title: 'Buy a cheaper house first',
        subtitle: () => 'Sold to put the whole of it down on the second.',
        wide: true,
        build: halfStepPurchase,
    },
];

/** The case every other one is compared with. */
const BASE = 0;

const model = {
    refresh: function () {
        const economy_context = {
            for_monthly_cost: forMonthlyCost,
            appreciator: APPRECIATOR,
            get_best_mortgage_detail: getBestMortgageDetail,
        };
        const scenario_context = {
            house_price: this.second_house_price,
            deposit: this.start_deposit,
            monthly_payment: this.monthly_payment,
        };

        scenarios = STRATEGIES.map((strategy) => ({
            title: strategy.title,
            subtitle: strategy.subtitle(scenario_context),
            wide: strategy.wide === true,
            scenario: strategy.build(economy_context, scenario_context),
        }));
    },
    _start_deposit: 100000,
    _second_house_price: 250000,
    _monthly_payment: 1500,
    get start_deposit() {
        return this._start_deposit;
    },
    set start_deposit(value) {
        this._start_deposit = value;
        this.refresh();
    },
    get second_house_price() {
        return this._second_house_price;
    },
    set second_house_price(value) {
        this._second_house_price = value;
        this.refresh();
    },
    get monthly_payment() {
        return this._monthly_payment;
    },
    set monthly_payment(value) {
        this._monthly_payment = value;
        this.refresh();
    },
};

function hasError(mortgage_lifetime: AnyMortgageLifetime) {
    return isErrored(mortgage_lifetime);
}

/**
 * Which case's schedule is open, as the place it sits rather than the mortgage
 * itself: the mortgages are rebuilt whenever an input changes, and the panel
 * should stay on the same case rather than close under you.
 */
let open_schedule: { option: number; lifetime: number } | null = null;

function isOpen(option: number, lifetime: number) {
    return (
        open_schedule !== null &&
        open_schedule.option === option &&
        open_schedule.lifetime === lifetime
    );
}

/**
 * The id of the card a schedule belongs to, so the panel can send you back up
 * to it. The panel sits under the whole grid, which on a wide screen is a
 * couple of screens away from the card that opened it.
 */
function cardId(option: number, lifetime: number) {
    return `case-${option}-${lifetime}`;
}

function scrollTo(id: string) {
    document
        .getElementById(id)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

const Scenario = {
    view: ({ attrs: { scenario, option } }) => {
        return scenario.mortgage_lifetimes.map((mortgage_lifetime, index) =>
            m(MortgageLifetimeCard, {
                mortgage_lifetime,
                id: cardId(option, index),
                open: isOpen(option, index),
                onopen: () => {
                    open_schedule = isOpen(option, index)
                        ? null
                        : { option, lifetime: index };
                },
            }),
        );
    },
};

const MortgageLifetimeCard = {
    view: ({ attrs: { mortgage_lifetime, id, open, onopen } }) => {
        const errored = hasError(mortgage_lifetime);
        // mortgage_detail is null when no product matched the LTV, and it has
        // to be guarded: the rates used to be read unconditionally and threw,
        // taking the page down.
        const detail = mortgage_lifetime.mortgage_detail;

        return m(
            '.card',
            {
                id,
                class: [
                    errored ? 'card--errored' : '',
                    // The card whose schedule is showing. Without it the only
                    // sign is the button's own wording, which is off screen by
                    // the time you are reading the panel.
                    open ? 'card--open' : '',
                ].join(' '),
            },
            m(
                '.card__head',
                m(
                    '.card__prices',
                    m('span', '🏠'),
                    m('span', asCurrency(mortgage_lifetime.house_price)),
                    m('span', '→'),
                    m(
                        'span',
                        errored
                            ? 'n/a'
                            : asCurrency(mortgage_lifetime.house_price_at_end),
                    ),
                ),
                errored
                    ? m(
                          '.card__figure',
                          m('.card__error', mortgage_lifetime.error_message),
                      )
                    : m(
                          '.card__figure',
                          m(
                              '.card__amount',
                              asCurrency(mortgage_lifetime.principal),
                          ),
                          m('.card__caption', 'borrowed'),
                      ),
                m(
                    '.card__rates',
                    detail === null
                        ? 'no product'
                        : `${detail.fixed_term}yr @ ${asPercent(
                              detail.initial_interest,
                          )} → ${asPercent(detail.subsequent_interest)}`,
                ),
            ),
            m(
                '.card__foot',
                m(
                    '.card__stat',
                    m('.card__stat-label', 'Interest'),
                    m(
                        '.card__stat-value.card__stat-value--cost',
                        errored
                            ? '£∞'
                            : asCurrency(mortgage_lifetime.loan_cost),
                    ),
                ),
                m(
                    '.card__stat',
                    m('.card__stat-label', 'Term'),
                    m(
                        '.card__stat-value',
                        errored ? '∞' : yearsMonths(mortgage_lifetime.term),
                    ),
                ),
                // A mortgage that cannot happen has no payments to show.
                errored
                    ? null
                    : m(
                          'button.card__link',
                          {
                              type: 'button',
                              'aria-expanded': String(open),
                              onclick: onopen,
                          },
                          open ? 'Hide the months' : 'Month by month',
                      ),
            ),
        );
    },
};

/**
 * The open case's schedule, in one panel under the grid rather than inside the
 * card that asked for it — a card is a column in a five-column grid, and six
 * columns of figures do not fit in one.
 *
 * It renders nothing when the case it was opened on has gone: an input can
 * change under it and turn that mortgage into one that cannot happen.
 */
const OpenSchedule = {
    view: ({ attrs: { scenarios } }) => {
        if (open_schedule === null) {
            return null;
        }

        const option = scenarios[open_schedule.option];
        const mortgage_lifetime =
            option?.scenario.mortgage_lifetimes[open_schedule.lifetime];
        if (!mortgage_lifetime || isErrored(mortgage_lifetime)) {
            return null;
        }

        const houses = option.scenario.mortgage_lifetimes.length;
        const card = cardId(open_schedule.option, open_schedule.lifetime);

        return m(
            '.panel.schedule-panel',
            {
                // Brings the panel to you when it opens, and again when another
                // card takes it over — that is a different schedule under the
                // same heading, and nothing else on screen would say so.
                oncreate: ({ dom, state }) => {
                    state.showing = card;
                    dom.scrollIntoView?.({
                        behavior: 'smooth',
                        block: 'nearest',
                    });
                },
                onupdate: ({ dom, state }) => {
                    if (state.showing !== card) {
                        state.showing = card;
                        dom.scrollIntoView?.({
                            behavior: 'smooth',
                            block: 'nearest',
                        });
                    }
                },
            },
            m('.schedule-panel__head', [
                m(
                    'h2.schedule-panel__title',
                    houses > 1
                        ? `${option.title} · ${
                              open_schedule.lifetime === 0 ? 'first' : 'second'
                          } house`
                        : option.title,
                ),
                m('.schedule-panel__controls', [
                    m(
                        'button.schedule-panel__back',
                        {
                            type: 'button',
                            onclick: () => scrollTo(card),
                        },
                        '↑ Back to the card',
                    ),
                    m(
                        'button.schedule-panel__close',
                        {
                            type: 'button',
                            'aria-label': 'Close the schedule',
                            onclick: () => {
                                open_schedule = null;
                            },
                        },
                        '×',
                    ),
                ]),
            ]),
            m(Schedule, { mortgage: mortgage_lifetime.mortgage }),
        );
    },
};

/**
 * "£3,240 less interest", coloured for which way it went. Both callers state
 * the direction rather than assuming a saving: an alternative that costs more
 * used to render as a negative saving, which read as nonsense.
 */
function interestPhrase(interest_saved: number) {
    const saved = interest_saved >= 0;
    return m(
        'span',
        { class: saved ? 'saving' : 'cost' },
        `${asCurrency(Math.abs(interest_saved))} ${
            saved ? 'less' : 'more'
        } interest`,
    );
}

/** "1yr 6mo", or "-1yr 6mo": yearsMonths cannot take a negative on its own. */
function signedYearsMonths(months: number) {
    return months < 0 ? `-${yearsMonths(-months)}` : yearsMonths(months);
}

/** "1 year and 2 months sooner", or "later", or neither when the terms match. */
function timingPhrase(months_saved: number) {
    if (months_saved === 0) {
        return 'in the same time';
    }
    return `${yearsMonthsLong(Math.abs(months_saved))} ${
        months_saved > 0 ? 'sooner' : 'later'
    }`;
}

/** What a case is worth against the base, in the box under its cards. */
const SavingSummary = {
    view: ({ attrs: { base, scenario } }) => {
        const comparison = compareScenarios(base, scenario);
        if (comparison === null) {
            return null;
        }

        return m('.callout', [
            'Compared with buying now, you will pay ',
            interestPhrase(comparison.interest_saved),
            ' and be mortgage free ',
            timingPhrase(comparison.months_saved),
            '.',
        ]);
    },
};

/**
 * Every case is the same block in the same grid — heading, subtitle, cards,
 * and what it is worth. The base case is the only one without a summary,
 * because it is the thing the others are compared with, and the two-house case
 * is the only one wide enough for its second card.
 */
const Scenarios = {
    view: ({ attrs: { scenarios } }) =>
        m(
            '.option-grid',
            scenarios.map((option, index) =>
                m(
                    '.option',
                    { class: option.wide ? 'option--wide' : '' },
                    m(
                        '.option__head',
                        m('h2.option__title', option.title),
                        m('p.option__subtitle', option.subtitle),
                    ),
                    m(
                        '.card-row',
                        m(Scenario, {
                            scenario: option.scenario,
                            option: index,
                        }),
                    ),
                    index === BASE
                        ? null
                        : m(SavingSummary, {
                              base: scenarios[BASE].scenario,
                              scenario: option.scenario,
                          }),
                ),
            ),
        ),
};

/**
 * The date every case is judged at, and the wallet they all share.
 *
 * The date is the longest of the terms; anything shorter would cut a case off
 * mid-mortgage. The wallet is the **base case's** deposit and monthly payment
 * — the money you were going to spend anyway on the house you asked for. A
 * case that wants more than that shows the difference as a negative, which is
 * what it is: money you had to find. Measuring against the largest of them
 * instead would hand every other case a windfall it never had.
 */
function horizonFor(scenarios: Option[]) {
    const base = scenarios[BASE].scenario;
    let months = 0;

    for (const { scenario } of scenarios) {
        months = Math.max(months, scenario.total_months ?? 0);
    }

    return {
        months,
        deposit: base.mortgage_lifetimes[0].deposit,
        monthly_payment: base.mortgage_lifetimes[0].monthly_cost,
    };
}

/**
 * An errored case has no term and no interest, so every cell that would have
 * been a figure is a dash rather than a 0 or an ∞ that could be read as one.
 */
const DASH = '—';

/** A figure, in the colour of which way it went. */
function delta(amount: number, text: string) {
    return m('td.numeric', { class: amount >= 0 ? 'saving' : 'cost' }, text);
}

/**
 * The cases as rows rather than cards: what each mortgage costs and how long
 * it runs. The cards are the argument, this is the scoreboard.
 */
const MortgageTable = {
    view: ({ attrs: { scenarios } }) => {
        const base = scenarios[BASE].scenario;

        return m(
            ScrollHint,
            { class: 'panel' },
            m(
                'table.data-table',
                m(
                    'thead',
                    m('tr', [
                        m('th', 'Case'),
                        heading(
                            'A month',
                            'What you hand the lender each month in this case.',
                            'left',
                        ),
                        heading(
                            'Interest',
                            'Everything you repay beyond the money you borrowed, over the whole mortgage. Both mortgages, for the case that buys twice.',
                            'left',
                        ),
                        heading(
                            'Interest saved',
                            'How much less interest than buying the house you want now.',
                        ),
                        heading(
                            'Term',
                            'How long until the debt is gone, at that monthly repayment.',
                        ),
                        heading(
                            'Sooner',
                            'How much earlier the debt is gone than buying the house you want now.',
                        ),
                    ]),
                ),
                m(
                    'tbody',
                    scenarios.map((option, index) => {
                        const scenario = option.scenario;
                        const interest = totalLoanCost(scenario);
                        const comparison =
                            index === BASE
                                ? null
                                : compareScenarios(base, scenario);

                        return m('tr', [
                            m('td', option.title),
                            m(
                                'td.numeric',
                                asCurrency(
                                    scenario.mortgage_lifetimes[0].monthly_cost,
                                ),
                            ),
                            m(
                                'td.numeric',
                                interest === null ? DASH : asCurrency(interest),
                            ),
                            comparison === null
                                ? m('td.numeric', DASH)
                                : delta(
                                      comparison.interest_saved,
                                      asCurrency(comparison.interest_saved),
                                  ),
                            m(
                                'td.numeric',
                                scenario.total_months === null
                                    ? DASH
                                    : yearsMonths(scenario.total_months),
                            ),
                            comparison === null
                                ? m('td.numeric', DASH)
                                : delta(
                                      comparison.months_saved,
                                      signedYearsMonths(
                                          comparison.months_saved,
                                      ),
                                  ),
                        ]);
                    }),
                ),
            ),
        );
    },
};

/**
 * The same cases, valued at one shared date, with the total broken into the
 * three things it is made of. A single figure nobody can take apart is a
 * figure nobody should trust — and taking it apart is what shows that four of
 * the five end up in the same house, and that the difference between them is
 * entirely cash.
 */
const WorthTable = {
    view: ({ attrs: { scenarios, horizon } }) =>
        m(
            ScrollHint,
            { class: 'panel' },
            m(
                'table.data-table',
                m(
                    'thead',
                    m('tr', [
                        m('th', 'Case'),
                        heading(
                            'House',
                            `What the house you end up in is worth at ${yearsMonthsLong(
                                horizon.months,
                            )}. A case that finishes early goes on appreciating until then, which is why most of these match.`,
                            'left',
                        ),
                        heading(
                            'Deposit kept',
                            `The deposit you did not have to put down, against the ${asCurrency(
                                horizon.deposit,
                            )} you were going to. Negative for a case that asks for more, because that is money you had to find.`,
                            'left',
                        ),
                        heading(
                            'While paying',
                            `Your ${asCurrency(
                                horizon.monthly_payment,
                            )} a month, less what this case actually paid out over its term. Negative for a case that repays more each month: that money is spent, and it is spent every month the mortgage runs.`,
                        ),
                        heading(
                            'Once it ends',
                            `The same ${asCurrency(
                                horizon.monthly_payment,
                            )} a month for the months between this case finishing and ${yearsMonthsLong(
                                horizon.months,
                            )}, when there is no mortgage left to pay it to and it stays in your pocket.`,
                        ),
                        heading(
                            'Total',
                            'The house plus the cash, for every case at the same date.',
                        ),
                    ]),
                ),
                m(
                    'tbody',
                    scenarios.map((option) => {
                        const worth = netWorthAtHorizon(
                            APPRECIATOR,
                            option.scenario,
                            horizon,
                        );

                        return m('tr', [
                            m('td', option.title),
                            m(
                                'td.numeric',
                                worth === null ? DASH : asCurrency(worth.house),
                            ),
                            // Negative for a case that wanted more deposit
                            // than you were going to put down, and coloured
                            // for it: that is money out, not money kept.
                            worth === null
                                ? m('td.numeric', DASH)
                                : m(
                                      'td.numeric',
                                      {
                                          class:
                                              worth.deposit_kept < 0
                                                  ? 'cost'
                                                  : '',
                                      },
                                      asCurrency(worth.deposit_kept),
                                  ),
                            worth === null
                                ? m('td.numeric', DASH)
                                : m(
                                      'td.numeric',
                                      {
                                          class:
                                              worth.budget_kept_paying < 0
                                                  ? 'cost'
                                                  : '',
                                      },
                                      asCurrency(worth.budget_kept_paying),
                                  ),
                            m(
                                'td.numeric',
                                worth === null
                                    ? DASH
                                    : asCurrency(worth.budget_kept_after),
                            ),
                            m(
                                'td.numeric.numeric--total',
                                worth === null ? DASH : asCurrency(worth.total),
                            ),
                        ]);
                    }),
                ),
            ),
        ),
};

/**
 * The two scoreboards. Two tables rather than one with ten columns: the
 * breakdown of what you are left holding needs four columns of its own, and
 * eleven columns of figures do not fit a page you can read without scrolling
 * it sideways.
 */
const Summary = {
    view: ({ attrs: { scenarios } }) => {
        const horizon = horizonFor(scenarios);

        return [
            m('h2.section-title', 'Every case side by side'),
            m(MortgageTable, { scenarios }),
            m(
                'h2.section-title',
                `Where each case leaves you at ${yearsMonthsLong(
                    horizon.months,
                )}`,
            ),
            m(WorthTable, { scenarios, horizon }),
        ];
    },
};

const SecondHousePriceInput = {
    view: () =>
        m(NumberInput, {
            value: model.second_house_price,
            onchange: (x) => {
                model.second_house_price = x;
            },
            step: 10000,
            chars: 9,
            id: 'second_house_price',
            text: 'The house I want costs',
            prefix: '£',
            group: true,
        }),
};

const MonthlyPaymentInput = {
    view: () =>
        m(NumberInput, {
            value: model.monthly_payment,
            onchange: (x) => {
                model.monthly_payment = x;
            },
            step: 100,
            chars: 7,
            id: 'monthly_payment',
            text: 'I can repay',
            prefix: '£',
            group: true,
            afterText: 'a month',
        }),
};

const DepositInput = {
    view: () =>
        m(NumberInput, {
            value: model.start_deposit,
            onchange: (x) => {
                model.start_deposit = x;
            },
            step: 10000,
            chars: 9,
            id: 'start_deposit',
            text: 'My deposit is',
            prefix: '£',
            group: true,
        }),
};

export const ScenariosPage = {
    oninit: () => model.refresh(),
    view: () =>
        m(
            Page,
            { title: 'Exploring some options' },
            m(
                'p.lede',
                `One big mortgage is not the only way to end up in the house you want. Put in your three numbers and each way of getting there is costed out below, measured against simply buying the house you want now. Houses are assumed to appreciate at ${asPercent(
                    APPRECIATION_RATE,
                )} a year.`,
            ),
            m(
                '.controls',
                m(DepositInput),
                m(SecondHousePriceInput),
                m(MonthlyPaymentInput),
            ),
            // Sits between the inputs and the cases: it is the sentence you
            // need at the moment you first meet a card, not one to hunt for at
            // the foot of the page.
            m(
                'p.prose',
                'Each card is one mortgage: what the house costs when you buy it and what it is worth by the time it is paid off, what you borrow once your deposit is down, and the rate you start on and move to when the fixed period ends.',
            ),
            scenarios.length === 0 ? null : m(Scenarios, { scenarios }),
            scenarios.length === 0 ? null : m(OpenSchedule, { scenarios }),
            scenarios.length === 0 ? null : m(Summary, { scenarios }),
            m(
                '.notes',
                m(
                    'p.prose',
                    'Interest is what the loan costs you on top of the price of the house, and term is how long it takes to clear at the repayment you set. Two cards under one heading are one plan, not two: the first house is sold and every penny of it becomes the deposit on the second.',
                ),
                m(
                    'p.prose',
                    'The second table judges every case at the same date, the longest of the terms, because a case that clears the debt early would otherwise show the smallest house simply for having had the least time to appreciate. Its cash columns are all measured against buying the house you want now — the deposit and the monthly payment you were going to spend anyway — so a case that asks for more money shows it as a negative rather than being handed it for free. Hover a heading for what each column is. Cash left over is assumed to sit there earning nothing, which is the one thing here that flatters a case that finishes early.',
                ),
                m(
                    'p.prose',
                    'A greyed-out card is a mortgage that cannot happen — either no lender offers a product at that loan to value, or the repayment is too small to ever clear the debt.',
                ),
                m(
                    'p.prose',
                    `Rates are the Bank of England's average of what lenders were quoting in ${RATES_AS_AT}, by fixed term and loan to value band, and what those deals revert to when the fix ends. They are the middle of a market rather than a product you can apply for, and they carry no arrangement fee — a real one usually does.`,
                ),
            ),
        ),
};
